import type { PermissionCode } from '@ldembalagens/types';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { PageLoader } from '@/components/common/page-loader';
import { useAuth, usePermissions } from '@/hooks/use-auth';
import { ForbiddenPage } from '@/pages/error-pages';

/** Exige sessão autenticada; redireciona para /login preservando o destino. */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'idle' || status === 'loading') return <PageLoader label="Restaurando sessão…" />;
  if (status !== 'authenticated')
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <Outlet />;
}

/** Exige tenant ativo. Superadmin sem tenant é levado à administração. */
export function RequireTenant() {
  const { tenant, isSuperAdmin } = useAuth();
  if (!tenant) return isSuperAdmin ? <Navigate to="/admin/empresas" replace /> : <ForbiddenPage />;
  return <Outlet />;
}

/** Exige ao menos uma das permissões informadas. */
export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: PermissionCode[];
  children?: ReactNode;
}) {
  const { canAny } = usePermissions();
  if (!canAny(...anyOf)) return <ForbiddenPage />;
  return children ? <>{children}</> : <Outlet />;
}

export function RequireSuperAdmin() {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <ForbiddenPage />;
  return <Outlet />;
}
